import { AlertTriangle, CheckCircle2, Globe, Play, Terminal, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { grantOffer } from "../lib/run-steps";
import { useTaal } from "@/contexts/TaalContext";
import { detailInTaal, stapInTaal } from "@/lib/codestappen-taal";

/**
 * What happened, as a list you read downwards.
 *
 * Built as a step list from the first day because the agent loop lands here
 * next and writes more kinds of step rather than a different screen. Today a
 * run makes four; later the same list holds "read main.py", "changed two
 * lines", "the test failed, trying again".
 *
 * The network step is the one that must never be quiet. Phase 2b measured
 * that pip prints its own retry warnings over our readable 403, so the
 * sentence travels on the result and this is the only place it can reach a
 * person. It sits above the output on purpose: reading downwards, you meet the
 * reason before the failure.
 */
export default function RunSteps({ steps, onGrant, granting }) {
  if (!steps.length) return null;

  return (
    <ol className="space-y-2" data-testid="run-steps">
      {steps.map((step) => (
        <li key={step.id} data-testid={`step-${step.kind}`}>
          {step.kind === "task" && (
            <Row icon={<Play className="h-4 w-4" />} title={step.title} detail={step.detail} />
          )}

          {step.kind === "network" && step.hosts?.length > 0 && (
            <NetworkRefusal step={step} onGrant={onGrant} granting={granting} />
          )}

          {step.kind === "network" && !step.hosts?.length && (
            <Row
              icon={<Globe className="h-4 w-4 text-ok" />}
              title={step.title}
              detail={step.detail}
            />
          )}

          {step.kind === "output" && <Output step={step} />}

          {step.kind === "result" && (
            <Row
              icon={
                step.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-ok" />
                ) : (
                  <XCircle className="h-4 w-4 text-destructive" />
                )
              }
              title={step.title}
              detail={step.detail}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

// GL223-1 (glasronde 0.9.223): de stappen staan in het Nederlands en worden hier vertaald, net als in AgentSteps.
function Row({ icon, title, detail }) {
  const { taal } = useTaal();
  title = stapInTaal(title, taal);
  detail = detailInTaal(detail, taal);
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="shrink-0">{icon}</span>
      {/* min-w-0, or a long title pushes the time off the row. */}
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {detail && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{detail}</span>}
    </div>
  );
}

function NetworkRefusal({ step, onGrant, granting }) {
  const { t, taal } = useTaal();
  const offer = grantOffer(step.hosts);
  return (
    <div
      className="rounded-lg border border-corona/40 bg-corona/10 p-3"
      data-testid="network-refusal"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-corona mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{stapInTaal(step.title, taal)}</p>
          {/* The sentence itself, never summarised away. */}
          <p className="mt-1 text-sm text-muted-foreground break-words">{detailInTaal(step.detail, taal)}</p>
        </div>
      </div>
      {offer && (
        <div className="mt-3">
          <Button
            type="button"
            size="sm"
            className="tap-target w-full sm:w-auto"
            disabled={granting}
            onClick={() => onGrant?.(offer)}
            data-testid="grant-network"
          >
            {granting ? t("Toestaan…") : offer.label}
          </Button>
          <p className="mt-1.5 text-xs text-muted-foreground">{offer.explains}</p>
        </div>
      )}
    </div>
  );
}

function Output({ step }) {
  const { t } = useTaal();
  return (
    <div className="rounded-lg border bg-muted/40" data-testid="run-output">
      <div className="flex items-center gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
        <Terminal className="h-3.5 w-3.5" />
        {t("Uitvoer")}
        {step.truncated && <span className="ml-auto">{t("in het midden ingekort")}</span>}
      </div>
      {step.stdout && (
        <pre className="overflow-x-auto px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap break-words">
          {step.stdout}
        </pre>
      )}
      {step.stderr && (
        <pre
          className="overflow-x-auto border-t px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap break-words text-destructive"
          data-testid="run-stderr"
        >
          {step.stderr}
        </pre>
      )}
    </div>
  );
}
